import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiSun, FiSettings, FiArrowRight } = FiIcons;

const CuttingSection = ({ nextStep, t }) => {
  const [cutQuality, setCutQuality] = useState(50);
  const [selectedCut, setSelectedCut] = useState('round');

  // Princess diamond image URL
  const princessDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758784431723-princess-diamond-transparent.png';
  // round diamond image URL
  const roundDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758715773888-round-diamond-transparent.png';
  // emerald diamond image URL
  const emeraldDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758715838353-emerald-diamond-transparent.png';
  // oval diamond image URL
  const ovalDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758782416993-oval-diamond.png';
  // pear diamond image URL
  const pearDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758785485792-pear-diamond.png';
  // marquise diamond image URL
  const marquiseDiamondUrl = 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758787636904-marquise-diamond.png';

  // ✅ Fixed cutTypes: corrected facet values
  const cutTypes = {
    round: {
      name: 'Round',
      facets: 58,
      sparkle: 95,
      size: 'w-32 h-32'
    },
    princess: {
      name: 'Princess',
      facets: 57,
      sparkle: 85,
      size: 'w-32 h-32'
    },
    emerald: {
      name: 'Emerald',
      facets: 57,
      sparkle: 70,
      size: 'w-32 h-32'
    },
    oval: {
      name: 'Oval',
      facets: 58,
      sparkle: 90,
      size: 'w-32 h-32'
    },
    marquise: {
      name: 'Marquise',
      facets: 58,
      sparkle: 88,
      size: 'w-32 h-32'
    },
    pear: {
      name: 'Pear',
      facets: 58,
      sparkle: 87,
      size: 'w-32 h-32'
    }
  };

  const getSparkleIntensity = () => {
    const baseSparkle = cutTypes[selectedCut].sparkle;
    return Math.min(100, baseSparkle * (cutQuality / 100));
  };

  const getCutQualityLabel = () => {
    if (cutQuality < 20) return t('poor') + ' - ' + t('poorDesc');
    if (cutQuality < 40) return t('fair') + ' - ' + t('fairDesc');
    if (cutQuality < 60) return t('good') + ' - ' + t('goodDesc');
    if (cutQuality < 80) return t('veryGood') + ' - ' + t('veryGoodDesc');
    return t('excellent') + ' - ' + t('excellentDesc');
  };

  // Get the glow intensity based on cut quality
  const getGlowIntensity = () => {
    return (cutQuality / 100) * 50; // Scale from 0 to 50px
  };

  // Get the glow opacity based on cut quality
  const getGlowOpacity = () => {
    return (cutQuality / 100) * 0.8; // Scale from 0 to 0.8
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-400 to-pantone-blue bg-clip-text text-transparent">
            {t('cuttingTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('cuttingDescription')}
          </p>
        </motion.div>

        {/* Interactive Diamond Simulator */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Diamond Display */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-gradient-to-br from-gray-900 to-black rounded-3xl p-12 border border-gray-700"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-white mb-4">{t('interactiveDiamond')}</h3>
                <p className="text-gray-400">{t('adjustQuality')}</p>
              </div>

              {/* Diamond Visualization */}
              <div className="relative flex items-center justify-center h-64">
                <motion.div
                  animate={{
                    rotate: 360,
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    rotate: {
                      duration: 10,
                      repeat: Infinity,
                      ease: "linear"
                    },
                    scale: {
                      duration: 2,
                      repeat: Infinity
                    }
                  }}
                  className="relative"
                >
                  {/* Outer Glow Effect - Multiple layers for better effect */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: getGlowOpacity() }}
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `radial-gradient(circle, 
                        rgba(255, 255, 255, ${getGlowOpacity() * 0.8}) 0%, 
                        rgba(59, 130, 246, ${getGlowOpacity() * 0.6}) 20%, 
                        rgba(147, 197, 253, ${getGlowOpacity() * 0.4}) 40%, 
                        rgba(255, 255, 255, ${getGlowOpacity() * 0.2}) 60%, 
                        transparent 80%
                      )`,
                      width: `${128 + getGlowIntensity() * 2}px`,
                      height: `${128 + getGlowIntensity() * 2}px`,
                      transform: 'translate(-50%, -50%)',
                      left: '50%',
                      top: '50%',
                      filter: `blur(${getGlowIntensity() / 4}px)`,
                      zIndex: 1
                    }}
                  />

                  {/* Secondary Glow Layer */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: getGlowOpacity() * 0.6 }}
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `radial-gradient(circle, 
                        rgba(255, 255, 255, ${getGlowOpacity() * 0.6}) 0%, 
                        rgba(59, 130, 246, ${getGlowOpacity() * 0.4}) 30%, 
                        transparent 70%
                      )`,
                      width: `${128 + getGlowIntensity() * 1.5}px`,
                      height: `${128 + getGlowIntensity() * 1.5}px`,
                      transform: 'translate(-50%, -50%)',
                      left: '50%',
                      top: '50%',
                      filter: `blur(${getGlowIntensity() / 6}px)`,
                      zIndex: 2
                    }}
                  />

                  {/* Diamond Container with proper z-index */}
                  <div className="relative z-10">
                    {/* ✅ Princess Diamond Image */}
                    {selectedCut === 'princess' ? (
                      <img
                        src={princessDiamondUrl}
                        alt="Princess Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}

                    {/* ✅ Round Diamond Image */}
                    {selectedCut === 'round' && (
                      <img
                        src={roundDiamondUrl}
                        alt="Round Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Round image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}

                    {/* ✅ Emerald Diamond Image */}
                    {selectedCut === 'emerald' && (
                      <img
                        src={emeraldDiamondUrl}
                        alt="Emerald Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Emerald image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}

                    {/* ✅ Oval Diamond Image */}
                    {selectedCut === 'oval' && (
                      <img
                        src={ovalDiamondUrl}
                        alt="Oval Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Oval image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}

                    {/* ✅ Pear Diamond Image */}
                    {selectedCut === 'pear' && (
                      <img
                        src={pearDiamondUrl}
                        alt="Pear Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Pear image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}

                    {/* ✅ Marquise Diamond Image */}
                    {selectedCut === 'marquise' && (
                      <img
                        src={marquiseDiamondUrl}
                        alt="Marquise Diamond"
                        className={`${cutTypes[selectedCut].size} object-contain drop-shadow-2xl relative z-10`}
                        style={{
                          filter: `contrast(1.1) saturate(1.2)`, // Keep diamond visible without brightness
                        }}
                        onError={(e) => {
                          console.error('Marquise image failed to load:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    )}

                    {/* Fallback diamond shape */}
                    <div
                      className={`${cutTypes[selectedCut].size} bg-gradient-to-br from-white via-pantone-blue/30 to-pantone-blue/50 shadow-2xl transform rotate-45 rounded-[2px] ${(selectedCut === 'princess' || selectedCut === 'round' || selectedCut === 'emerald' || selectedCut === 'oval' || selectedCut === 'pear' || selectedCut === 'marquise') ? 'hidden' : 'block'} relative z-10`}
                    />
                  </div>
                </motion.div>
              </div>

              {/* Sparkle Effects - positioned around the diamond */}
              {[...Array(Math.floor(getSparkleIntensity() / 10))].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    rotate: Math.random() * 360
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: Math.random() * 2
                  }}
                  className="absolute w-2 h-2 bg-white rounded-full z-20"
                  style={{
                    left: `${Math.random() * 80 + 10}%`,
                    top: `${Math.random() * 80 + 10}%`,
                    boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)'
                  }}
                />
              ))}

              {/* Sparkle Meter */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">{t('brilliance')}</span>
                  <span className="text-sm text-yellow-400">{Math.round(getSparkleIntensity())}%</span>
                </div>
                <div className="bg-gray-700 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${getSparkleIntensity()}%` }}
                    className="bg-gradient-to-r from-yellow-400 to-pantone-blue h-full rounded-full"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Controls */}
          <div className="space-y-8">
            {/* Cut Quality Slider */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiSettings} className="w-6 h-6 text-yellow-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">{t('cutQuality')}</h3>
              </div>
              <div className="space-y-4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={cutQuality}
                  onChange={(e) => setCutQuality(Number(e.target.value))}
                  className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-400">
                  <span>{t('poor')}</span>
                  <span>{t('fair')}</span>
                  <span>{t('good')}</span>
                  <span>{t('veryGood')}</span>
                  <span>{t('excellent')}</span>
                </div>
                {/* Fixed height container to prevent layout shift */}
                <div className="min-h-[3rem] flex items-center">
                  <p className="text-gray-300 leading-relaxed">
                    {getCutQualityLabel()}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Cut Shapes */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-pantone-blue/80 mr-3" />
                <h3 className="text-xl font-semibold text-white">{t('diamondShapes')}</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(cutTypes).map(([key, cut]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCut(key)}
                    className={`p-3 rounded-lg border transition-all duration-300 text-left transform hover:scale-105 ${
                      selectedCut === key
                        ? 'bg-gradient-to-r from-pantone-blue/40 to-blue-500/40 border-pantone-blue border-2 text-white shadow-lg shadow-pantone-blue/30 ring-2 ring-pantone-blue/50'
                        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className={`font-semibold ${selectedCut === key ? 'text-white' : ''}`}>
                      {cut.name}
                    </div>
                    <div className={`text-sm ${selectedCut === key ? 'text-blue-100' : 'opacity-75'}`}>
                      {cut.facets} facets
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Process Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-orange-600/20 to-yellow-600/20 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6"
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiSun} className="w-6 h-6 text-orange-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">{t('cuttingProcess')}</h3>
              </div>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {t('planning')}
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {t('cleaving')}
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {t('shaping')}
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {t('polishing')}
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <button
            onClick={nextStep}
            className="group bg-gradient-to-r from-yellow-600 to-pantone-blue hover:from-pantone-blue hover:to-yellow-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            {t('nextCertification')}
            <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default CuttingSection;